import React from "react";
import { Container, Skeleton } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import styles from "./guidesTableStyles.js";
import NoData from '../../../components/SharedComponents/NoData';
import GuidesTableActions from "../GuidesTableActions/index.js";

const GuidesTable = ({
  guides = [],
  isLoading,
  search,
  fetchGuides,
  fetchUnassingnedGuides,
  setHasItemsSelected,
  setSelectedGuides,
  selectedGuides = [],
}) => {

  const columns = [
    {
      field: "id",
      headerName: "ID",
      minWidth: 50,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>ID</strong>,
    },
    {
      field: "tracking_code",
      headerName: "CÓDIGO",
      minWidth: 120,
      flex: 1,
      resizable: false,
      sortable: false,
      renderHeader: (params) => <strong>CÓDIGO</strong>,
    },
    {
      field: "area",
      headerName: "AREA",
      type: "string",
      minWidth: 70,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>AREA</strong>,
    },
    {
      field: "zone",
      headerName: "ZONA",
      type: "string",
      minWidth: 70,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ZONA</strong>,
    },
    {
      field: "sector",
      headerName: "SECTOR",
      type: "string",
      minWidth: 90,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SECTOR</strong>,
    },
    {
      field: "subsector",
      headerName: "SUB",
      type: "string",
      minWidth: 110,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>SUB</strong>,
    },
    {
      field: "package_weight",
      headerName: "PESO",
      type: "string",
      minWidth: 60,
      flex: 1,
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>PESO</strong>,
    },
    {
      field: "actions",
      headerName: "ACCIONES",
      type: "string",
      resizable: false,
      sortable: false,
      headerAlign: "left",
      align: "left",
      renderHeader: (params) => <strong>ACCIONES</strong>,
      renderCell: (params) => <GuidesTableActions fetchGuides={fetchGuides} fetchUnassingnedGuides={fetchUnassingnedGuides} guide={params.row} />,
    },
  ];

  const filteredRows = guides.filter((guide) => {
    const transformedUser = {
      ...guide,
    };

    return Object.values(transformedUser).some((value) =>
      value.toString().toLowerCase().includes(search.toLowerCase())
    );
  });

  const rows = filteredRows?.map((guide, index) => ({
    id: guide.id_guide,
    tracking_code: guide.tracking_code,
    paternalLastname: guide.paternal_lastname,
    maternalLastname: guide.maternal_lastname,
    zone: guide.zone,
    area: guide.area,
    sector: guide.sector,
    subsector: guide.subsector,
    postal_code: guide.postal_code,
    ext_number: guide.ext_number,
    package_weight: guide.package_weight,
    latitude: guide.latitude,
    longitude: guide.longitude,
    id_recipient: guide.id_recipient,
  }));

  const handleSelectionChange = (newSelection) => {
    // Combinar las nuevas selecciones con las selecciones previas que no están visibles
    const currentVisibleIds = rows.map(row => row.id);
    const previousNonVisibleSelections = selectedGuides.filter(id => 
      !currentVisibleIds.includes(id)
    );
    
    const combinedSelections = [...new Set([...previousNonVisibleSelections, ...newSelection])];
    
    setSelectedGuides(combinedSelections);
    setHasItemsSelected(combinedSelections.length > 0);
  };

  // Solo las selecciones que son visibles en las filas actuales
  const visibleSelections = selectedGuides.filter(id => 
    rows.some(row => row.id === id)
  );

  return (
    <Container sx={styles.mainContainer}>
      {isLoading ? (
        <Container sx={styles.skeletonMainContainer}>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'20%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'10%'} height={40} />
            <Skeleton variant="rounded" width={'15%'} height={40} />
          </Container>
          <Container sx={styles.skeletonSecondaryContainer}>
            <Skeleton variant="rounded" width={'5%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'20%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'10%'} height={300} />
            <Skeleton variant="rounded" width={'15%'} height={300} />
          </Container>
        </Container>
      ) : (
        <DataGrid
          autoHeight
          checkboxSelection
          disableColumnFilter
          rows={rows}
          columns={columns}
          rowSelectionModel={visibleSelections}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          isRowSelectable={(params) =>
            params.row.latitude !== 0 && params.row.longitude !== 0
          }
          onRowSelectionModelChange={handleSelectionChange}
          disableColumnMenu={true}
          disableSelectionOnClick={true}
          localeText={{ 
            noRowsLabel: <NoData title="No se encontraron guías" />
          }}
          getRowClassName={(params) =>
            params.row.latitude === 0 && params.row.longitude === 0 ? 'unselectableRow' : ''
          }
          getRowHeight={() => 'auto'} 
          sx={{
            '& .MuiDataGrid-row.unselectableRow': styles.unselectableRow,
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { 
              py: '8px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { 
              py: '6px', 
              display: 'flex', 
              alignItems: 'center' 
            },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { 
              py: '22px', 
              display: 'flex', 
              alignItems: 'center' 
            },
          }}
        />
      )}
    </Container>
  );
};

export default GuidesTable;
